import { useEffect, useState } from "react";

/**
 * Edit a small ordered list of strings — default shift tasks, shift-end
 * questions. Rows are edited in place and saved as one array; the parent
 * owns the PUT so this card works against any record and any column.
 */
export const StringListCard = ({ title, help, items, addLabel, placeholder, onSave }: {
    title: string;
    help: string;
    items: string[];
    addLabel: string;
    placeholder: string;
    onSave: (items: string[]) => Promise<void>;
}) => {
    const [rows, setRows] = useState<string[]>(items);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    // A different participant selected = a different list.
    useEffect(() => { setRows(items); setSaved(false); setError(null); }, [items]);

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await onSave(rows.map((r) => r.trim()).filter((r) => r.length >= 2));
            setSaved(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the list");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head"><h2>{title}</h2></div>
            <div className="card-body" style={{ display: "grid", gap: "0.45rem" }}>
                {error && <div className="error-banner">{error}</div>}
                <p style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{help}</p>
                {rows.map((row, i) => (
                    <div key={i} style={{ display: "flex", gap: "0.4rem" }}>
                        <input value={row} placeholder={placeholder} style={{ flex: 1 }}
                            aria-label={`${title} item ${i + 1}`}
                            onChange={(e) => { setSaved(false); setRows(rows.map((r, j) => (j === i ? e.target.value : r))); }} />
                        <button className="icon-btn danger" title="Remove this item"
                            onClick={() => { setSaved(false); setRows(rows.filter((_, j) => j !== i)); }}>
                            Remove
                        </button>
                    </div>
                ))}
                {rows.length === 0 && (
                    <p style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Nothing set yet.</p>
                )}
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.2rem" }}>
                    <button className="btn ghost small" onClick={() => { setSaved(false); setRows([...rows, ""]); }}>
                        + {addLabel}
                    </button>
                    <button className="btn small" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : saved ? "Saved ✓" : "Save list"}
                    </button>
                </div>
            </div>
        </div>
    );
};
